import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Package, Folder, ArrowRight, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { db, GoalTemplate, MatterTemplate, ProjectTemplate, MatterType } from '../../lib/db';
import { TemplateForm } from '../templates/TemplateForm';
import { CreateMatterTemplateModal } from '../templates/CreateMatterTemplateModal';
import { CreateProjectTemplateModal } from '../templates/CreateProjectTemplateModal';
import { CreateFromMatterTemplateModal } from '../templates/CreateFromMatterTemplateModal';
import { CreateFromProjectTemplateModal } from '../templates/CreateFromProjectTemplateModal';
import { TemplateDetailsModal } from '../templates/TemplateDetailsModal';

type Props = {
  userId: string;
};

// This component encapsulates the previously large Templates section from SettingsPage
// so SettingsPage itself stays under 500 lines. The behavior is unchanged; we just
// moved the logic here and added a few guiding comments for clarity.
export default function TemplatesSettings({ userId }: Props) {
  const [templates, setTemplates] = useState<GoalTemplate[]>([]);
  const [userTemplates, setUserTemplates] = useState<GoalTemplate[]>([]);
  const [matterTemplates, setMatterTemplates] = useState<MatterTemplate[]>([]);
  const [projectTemplates, setProjectTemplates] = useState<ProjectTemplate[]>([]);
  const [matterTypes, setMatterTypes] = useState<MatterType[]>([]);
  const [hiddenTemplates, setHiddenTemplates] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<GoalTemplate | null>(null);
  const [showMatterModal, setShowMatterModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedMatterTemplate, setSelectedMatterTemplate] = useState<MatterTemplate | null>(null);
  const [selectedProjectTemplate, setSelectedProjectTemplate] = useState<ProjectTemplate | null>(null);
  const [activeTemplateTab, setActiveTemplateTab] = useState<'deliverables' | 'matters' | 'projects'>('deliverables');
  const [selectedDetailsTemplate, setSelectedDetailsTemplate] = useState<GoalTemplate | null>(null);

  useEffect(() => {
    loadData();
  }, [userId]);

  // Helper to load everything shown in this page section
  const loadData = async () => {
    try {
      setLoading(true);
      const allTemplates = await db.goalTemplates.findAll();
      setTemplates(allTemplates.filter(t => t.is_system));
      setUserTemplates(await db.goalTemplates.findByUserId(userId));
      setMatterTemplates(await db.matterTemplates.findByUserId(userId));
      setProjectTemplates(await db.projectTemplates.findByUserId(userId));
      setMatterTypes(await db.matterTypes.findByUserId(userId));
      const hidden = await db.userHiddenTemplates.findByUserId(userId);
      setHiddenTemplates(new Set(hidden.map(h => h.goal_template_id)));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const toggleTemplateVisibility = async (templateId: string) => {
    try {
      if (hiddenTemplates.has(templateId)) {
        await db.userHiddenTemplates.unhide(userId, templateId);
        const next = new Set(hiddenTemplates);
        next.delete(templateId);
        setHiddenTemplates(next);
        toast.success('Template is now visible');
      } else {
        await db.userHiddenTemplates.hide(userId, templateId);
        setHiddenTemplates(new Set([...hiddenTemplates, templateId]));
        toast.success('Template is now hidden');
      }
    } catch (error) {
      console.error('Error toggling template visibility:', error);
      toast.error('Failed to update template visibility');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template? This action cannot be undone.')) return;
    try {
      await db.taskTemplates.deleteByGoalTemplateId(templateId, userId);
      await db.goalTemplates.delete(templateId, userId);
      await loadData();
      toast.success('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleFormSubmit = async () => {
    setShowForm(false);
    setEditingTemplate(null);
    await loadData();
  };

  if (loading) {
    return (
      <div className="text-gray-500">Loading templates...</div>
    );
  }

  return (
    <>
      {/* Sub-tabs */}
      <div className="mb-6">
        <div className="flex gap-4 flex-wrap items-center">
          <button
            onClick={() => setActiveTemplateTab('deliverables')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTemplateTab === 'deliverables' ? 'bg-blue-500 text-white' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Deliverable Templates ({templates.length + userTemplates.length})
          </button>
          <button
            onClick={() => setActiveTemplateTab('matters')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTemplateTab === 'matters' ? 'bg-purple-500 text-white' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Matter Templates ({matterTemplates.length})
          </button>
          <button
            onClick={() => setActiveTemplateTab('projects')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTemplateTab === 'projects' ? 'bg-green-500 text-white' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Project Templates ({projectTemplates.length})
          </button>

          <div className="ml-auto flex gap-2">
            {activeTemplateTab === 'deliverables' && (
              <button onClick={() => setShowForm(true)} className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> New Deliverable
              </button>
            )}
            {activeTemplateTab === 'matters' && (
              <button onClick={() => setShowMatterModal(true)} className="flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" /> New Matter
              </button>
            )}
            {activeTemplateTab === 'projects' && (
              <button onClick={() => setShowProjectModal(true)} className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" /> New Project
              </button>
            )}
          </div>
        </div>
      </div>

      {showForm ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <TemplateForm
            userId={userId}
            template={editingTemplate}
            onClose={() => {
              setShowForm(false);
              setEditingTemplate(null);
            }}
            onSubmit={handleFormSubmit}
          />
        </div>
      ) : (
        <div className="grid gap-4">
          {/* Deliverables */}
          {activeTemplateTab === 'deliverables' && (
            <>
              {templates.length > 0 && (
                <>
                  <h3 className="text-lg font-semibold text-gray-900">System Templates</h3>
                  {templates.map(template => (
                    <div key={template.id} className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{template.title}</h3>
                          {template.description && (
                            <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2">
                            {template.category && (
                              <span className="text-xs px-2 py-1 bg-gray-100 rounded">{template.category}</span>
                            )}
                            <span className="text-xs text-gray-500">System Template</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelectedDetailsTemplate(template)}
                            className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
                          >
                            Details
                          </button>
                          <button
                            onClick={() => toggleTemplateVisibility(template.id)}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                            title={hiddenTemplates.has(template.id) ? 'Show template' : 'Hide template'}
                          >
                            {hiddenTemplates.has(template.id) ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              <h3 className="text-lg font-semibold text-gray-900 mt-4">Your Templates</h3>
              {userTemplates.length === 0 ? (
                <div className="p-6 bg-white border border-gray-200 rounded-lg text-gray-600 text-sm">
                  You have no custom templates yet. Click "New Deliverable" to create one.
                </div>
              ) : (
                userTemplates.map(template => (
                  <div key={template.id} className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{template.title}</h3>
                        {template.description && (
                          <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          {template.category && (
                            <span className="text-xs px-2 py-1 bg-gray-100 rounded">{template.category}</span>
                          )}
                          <span className="text-xs text-gray-500">Custom Template</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedDetailsTemplate(template)}
                          className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
                        >
                          Details
                        </button>
                        <button
                          onClick={() => { setEditingTemplate(template); setShowForm(true); }}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                          title="Edit template"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete template"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {/* Matter templates */}
          {activeTemplateTab === 'matters' && (
            <>
              {matterTemplates.length === 0 ? (
                <div className="p-6 bg-white border border-gray-200 rounded-lg text-center">
                  <p className="text-gray-600 mb-3">Create a matter template to quickly set up common work.</p>
                  <button onClick={() => setShowMatterModal(true)} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700">
                    <Folder className="w-4 h-4 mr-2" /> Create Your First Matter Template
                  </button>
                </div>
              ) : (
                matterTemplates.map(template => (
                  <div key={template.id} className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Folder className="w-5 h-5 text-purple-600" />
                          <h3 className="font-semibold text-gray-900">{template.title}</h3>
                        </div>
                        {template.description && <p className="text-sm text-gray-600 mt-1">{template.description}</p>}
                        <span className="text-xs text-gray-500 mt-2 inline-block">{template.is_system ? 'System Template' : 'Custom Template'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setSelectedMatterTemplate(template)} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100" title="Use this template">
                          <ArrowRight className="w-4 h-4" /> Use Template
                        </button>
                        {!template.is_system && (
                          <button onClick={() => db.matterTemplates.delete(template.id, userId).then(loadData)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete template">
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {/* Project templates */}
          {activeTemplateTab === 'projects' && (
            <>
              {projectTemplates.length === 0 ? (
                <div className="p-6 bg-white border border-gray-200 rounded-lg text-center">
                  <p className="text-gray-600 mb-3">Create a project template to set up common project structures.</p>
                  <button onClick={() => setShowProjectModal(true)} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">
                    <Package className="w-4 h-4 mr-2" /> Create Your First Project Template
                  </button>
                </div>
              ) : (
                projectTemplates.map(template => (
                  <div key={template.id} className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Package className="w-5 h-5 text-green-600" />
                          <h3 className="font-semibold text-gray-900">{template.title}</h3>
                        </div>
                        {template.description && <p className="text-sm text-gray-600 mt-1">{template.description}</p>}
                        <span className="text-xs text-gray-500 mt-2 inline-block">{template.is_system ? 'System Template' : 'Custom Template'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setSelectedProjectTemplate(template)} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100" title="Use this template">
                          <ArrowRight className="w-4 h-4" /> Use Template
                        </button>
                        {!template.is_system && (
                          <button onClick={() => db.projectTemplates.delete(template.id, userId).then(loadData)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete template">
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}

      {/* Modals */}
      <CreateMatterTemplateModal
        isOpen={showMatterModal}
        onClose={() => setShowMatterModal(false)}
        userId={userId}
        goalTemplates={[...templates.filter(t => !hiddenTemplates.has(t.id)), ...userTemplates]}
        onSuccess={() => {
          loadData();
          setShowMatterModal(false);
          toast.success('Matter template created successfully');
        }}
      />

      <CreateProjectTemplateModal
        isOpen={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        userId={userId}
        goalTemplates={[...templates.filter(t => !hiddenTemplates.has(t.id)), ...userTemplates]}
        matterTemplates={matterTemplates}
        onSuccess={() => {
          loadData();
          setShowProjectModal(false);
          toast.success('Project template created successfully');
        }}
      />

      <CreateFromMatterTemplateModal
        isOpen={!!selectedMatterTemplate}
        onClose={() => setSelectedMatterTemplate(null)}
        template={selectedMatterTemplate}
        userId={userId}
        onSuccess={() => {
          setSelectedMatterTemplate(null);
          toast.success('Navigate to Matters page to see your new matter');
        }}
      />

      <CreateFromProjectTemplateModal
        isOpen={!!selectedProjectTemplate}
        onClose={() => setSelectedProjectTemplate(null)}
        template={selectedProjectTemplate}
        userId={userId}
        onSuccess={() => {
          setSelectedProjectTemplate(null);
          toast.success('Navigate to Projects page to see your new project');
        }}
      />

      {selectedDetailsTemplate && (
        <TemplateDetailsModal
          template={selectedDetailsTemplate}
          userId={userId}
          onClose={() => setSelectedDetailsTemplate(null)}
        />
      )}
    </>
  );
}

